//=require node_modules/plyr/dist/plyr.min.js
//=require node_modules/glightbox/dist/js/glightbox.min.js

/**
 * Full Width Video — muted autoplay when ~50% of the video is in view.
 * Supports YouTube and Vimeo oEmbed iframes.
 * Keeps a poster + spinner overlay up until playback starts.
 * A play button opens the same video, with sound, in a lightbox.
 */
(function () {
  var reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  function post(iframe, payload) {
    if (!iframe || !iframe.contentWindow) {
      return;
    }
    iframe.contentWindow.postMessage(JSON.stringify(payload), "*");
  }

  function play(iframe) {
    var provider = iframe.getAttribute("data-video-provider");

    if (provider === "youtube") {
      post(iframe, { event: "command", func: "mute", args: [] });
      post(iframe, { event: "command", func: "playVideo", args: [] });
      return;
    }

    if (provider === "vimeo") {
      post(iframe, { method: "setVolume", value: 0 });
      post(iframe, { method: "play" });
    }
  }

  function pause(iframe) {
    var provider = iframe.getAttribute("data-video-provider");

    if (provider === "youtube") {
      post(iframe, { event: "command", func: "pauseVideo", args: [] });
      return;
    }

    if (provider === "vimeo") {
      post(iframe, { method: "pause" });
    }
  }

  function getContainer(section, iframe) {
    return (
      section.querySelector(".embed-responsive") ||
      iframe.parentElement ||
      section
    );
  }

  function ensurePoster(container, iframe) {
    var posterUrl = iframe.getAttribute("data-video-poster");
    if (!posterUrl) {
      return null;
    }

    container.classList.add("has-video-poster");
    container.style.setProperty("--video-poster", 'url("' + posterUrl + '")');

    var poster = container.querySelector(".full_width_video__poster");
    if (!poster) {
      poster = document.createElement("div");
      poster.className = "full_width_video__poster";
      poster.setAttribute("aria-hidden", "true");
      poster.innerHTML =
        '<span class="full_width_video__spinner" aria-hidden="true"></span>';
      container.appendChild(poster);
    } else if (!poster.querySelector(".full_width_video__spinner")) {
      var spinner = document.createElement("span");
      spinner.className = "full_width_video__spinner";
      spinner.setAttribute("aria-hidden", "true");
      poster.appendChild(spinner);
    }

    return poster;
  }

  function setLoading(container, isLoading) {
    if (!container) {
      return;
    }
    container.classList.toggle("is-loading", isLoading);
    if (isLoading) {
      container.setAttribute("aria-busy", "true");
    } else {
      container.removeAttribute("aria-busy");
    }
  }

  function revealVideo(iframe, poster, container) {
    iframe.classList.add("is-ready");
    if (poster) {
      poster.classList.add("is-hidden");
    }
    setLoading(container, false);
  }

  function holdPoster(iframe, poster) {
    iframe.classList.remove("is-ready");
    if (poster) {
      poster.classList.remove("is-hidden");
    }
  }

  function listenForPlaying(iframe, onPlaying) {
    var provider = iframe.getAttribute("data-video-provider");
    var done = false;

    function finish() {
      if (done) {
        return;
      }
      done = true;
      window.removeEventListener("message", onMessage);
      onPlaying();
    }

    function onMessage(event) {
      if (event.source !== iframe.contentWindow || !event.data) {
        return;
      }

      var data = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          return;
        }
      }

      if (provider === "youtube") {
        // 1 = playing
        if (data.event === "onStateChange" && data.info === 1) {
          finish();
        }
        return;
      }

      if (provider === "vimeo" && data.event === "play") {
        finish();
      }
    }

    window.addEventListener("message", onMessage);

    if (provider === "vimeo") {
      post(iframe, { method: "addEventListener", value: "play" });
    }

    // Fallback if the player never posts a play event.
    window.setTimeout(finish, 1200);
  }

  /**
   * Prefer API play (no reload). Fall back to autoplay src only if needed.
   */
  function startMuted(iframe, poster, container) {
    holdPoster(iframe, poster);
    setLoading(container, true);
    listenForPlaying(iframe, function () {
      revealVideo(iframe, poster, container);
    });

    if (iframe.dataset.autoplayArmed === "1") {
      play(iframe);
      return;
    }

    iframe.dataset.autoplayArmed = "1";
    play(iframe);

    // If the API call is ignored (common before handshake), arm autoplay via src
    // while the poster covers the reload.
    window.setTimeout(function () {
      if (iframe.classList.contains("is-ready")) {
        return;
      }

      try {
        var url = new URL(iframe.getAttribute("src"), window.location.href);
        if (url.searchParams.get("autoplay") === "1") {
          play(iframe);
          return;
        }

        url.searchParams.set("autoplay", "1");
        url.searchParams.set("playsinline", "1");
        if (iframe.getAttribute("data-video-provider") === "youtube") {
          url.searchParams.set("mute", "1");
        } else if (iframe.getAttribute("data-video-provider") === "vimeo") {
          url.searchParams.set("muted", "1");
        }

        holdPoster(iframe, poster);
        setLoading(container, true);
        iframe.src = url.toString();
      } catch (e) {
        play(iframe);
      }
    }, 400);
  }

  function isMostlyVisible(el) {
    if (!el) {
      return false;
    }
    var rect = el.getBoundingClientRect();
    if (!rect.height) {
      return false;
    }
    var viewH = window.innerHeight || document.documentElement.clientHeight;
    var visible = Math.min(rect.bottom, viewH) - Math.max(rect.top, 0);
    return visible / rect.height >= 0.75;
  }

  function lightboxWidth() {
    var maxW = window.innerWidth * (window.innerWidth < 768 ? 1 : 0.92);
    var maxH = window.innerHeight * 0.82;
    return Math.round(Math.min(maxW, maxH * (16 / 9))) + "px";
  }

  function silenceLightbox() {
    document
      .querySelectorAll(".glightbox-container iframe")
      .forEach(function (frame) {
        frame.src = "about:blank";
      });
    document
      .querySelectorAll(".glightbox-container video")
      .forEach(function (video) {
        video.pause();
        video.removeAttribute("src");
        video.load();
      });
  }

  function playUnmuted(player) {
    if (!player || typeof player.play !== "function") {
      return;
    }
    try {
      player.muted = false;
      var result = player.play();
      if (result && typeof result.catch === "function") {
        result.catch(function () {});
      }
    } catch (e) {}
  }

  function bindLightbox(section, iframe, state) {
    var button = section.querySelector(".full_width_video__play");
    var href = iframe.getAttribute("data-video-watch");
    var provider = iframe.getAttribute("data-video-provider");

    if (!button) {
      return;
    }

    if (!href || typeof window.GLightbox !== "function") {
      button.hidden = true;
      return;
    }

    button.addEventListener("click", function () {
      if (state.open) {
        return;
      }

      state.open = true;
      state.suspended = true;
      state.resume = state.wasPlaying;
      if (state.wasPlaying) {
        pause(iframe);
      }

      var lightbox = window.GLightbox({
        elements: [
          {
            href: href,
            type: "video",
            videoProvider: provider,
          },
        ],
        autoplayVideos: true,
        closeOnOutsideClick: true,
        closeEffect: "none",
        touchNavigation: false,
        keyboardNavigation: true,
        draggable: false,
        loop: false,
        videosWidth: lightboxWidth(),
        plyr: {
          css: "https://cdn.plyr.io/3.6.12/plyr.css",
          js: "https://cdn.plyr.io/3.6.12/plyr.js",
          config: {
            muted: false,
            autoplay: true,
            clickToPlay: true,
            loadSprite: true,
            iconUrl:
              section.getAttribute("data-plyr-sprite") ||
              "https://cdn.plyr.io/3.6.12/plyr.svg",
            ratio: "16:9",
            fullscreen: { enabled: true, iosNative: true },
            controls: [
              "play-large",
              "play",
              "progress",
              "current-time",
              "mute",
              "volume",
              "fullscreen",
            ],
            youtube: {
              noCookie: true,
              rel: 0,
              showinfo: 0,
              iv_load_policy: 3,
            },
            vimeo: {
              byline: false,
              portrait: false,
              title: false,
              transparent: false,
            },
          },
        },
        afterSlideLoad: function (data) {
          playUnmuted(data && data.player);
        },
        onClose: function () {
          state.open = false;
          state.suspended = false;
          if (
            state.resume &&
            isMostlyVisible(getContainer(section, iframe))
          ) {
            play(iframe);
            state.wasPlaying = true;
          } else if (state.resume) {
            state.wasPlaying = false;
          }
          state.resume = false;
          window.setTimeout(function () {
            lightbox.destroy();
          }, 0);
        },
      });

      lightbox.on("slide_changed", function (data) {
        var current = data && data.current;
        playUnmuted(current && current.player);
      });

      function onPointerDown(event) {
        if (!state.open) {
          return;
        }
        var root = document.querySelector(".glightbox-container");
        if (!root || !root.contains(event.target)) {
          return;
        }
        if (event.target.closest(".gclose")) {
          silenceLightbox();
          return;
        }
        if (
          event.target.closest(".ginner-container") ||
          event.target.closest(".gbtn")
        ) {
          return;
        }
        silenceLightbox();
        lightbox.close();
      }

      function onKeyDown(event) {
        if (!state.open) {
          return;
        }
        if (event.key === "Escape" || event.keyCode === 27) {
          silenceLightbox();
        }
      }

      document.addEventListener("click", onPointerDown, true);
      document.addEventListener("keydown", onKeyDown, true);

      lightbox.on("close", function () {
        document.removeEventListener("click", onPointerDown, true);
        document.removeEventListener("keydown", onKeyDown, true);
      });

      lightbox.open();
    });
  }

  function initSection(section) {
    var iframe = section.querySelector("iframe.js-scroll-autoplay-video");
    var button = section.querySelector(".full_width_video__play");

    if (!iframe) {
      if (button) {
        button.hidden = true;
      }
      return;
    }

    var state = {
      wasPlaying: false,
      suspended: false,
      resume: false,
      open: false,
    };

    bindLightbox(section, iframe, state);

    if (reduceMotion) {
      return;
    }

    var container = getContainer(section, iframe);
    var poster = ensurePoster(container, iframe);

    // Cover the initial player chrome/boot with the still.
    holdPoster(iframe, poster);

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (state.suspended) {
            return;
          }
          if (entry.isIntersecting) {
            if (!state.wasPlaying) {
              startMuted(iframe, poster, container);
              state.wasPlaying = true;
            }
          } else if (state.wasPlaying) {
            pause(iframe);
            setLoading(container, false);
            state.wasPlaying = false;
          }
        });
      },
      { threshold: 0.75 },
    );

    observer.observe(container);
  }

  function init() {
    document.querySelectorAll(".full_width_video").forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
